import { CheckCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card } from '@/components/public/Section';
import { Button } from '@/components/ui/button';
import { Field, FormError, SelectField, TextareaField } from '@/components/ui/field';
import { useBlurValidation } from '@/hooks/use-blur-validation';
import { api } from '@/lib/api';
import { NAP } from '@/lib/public-content';
import {
  BATHROOM_OPTIONS,
  BEDROOM_OPTIONS,
  DEFAULT_STATE,
  HONEYPOT_FIELD,
  MAX_PROPERTIES,
  SQUARE_FEET_BAND_OPTIONS,
  US_STATES,
  quoteRequestSchema,
} from '@shared/schemas/intake';

/*
 * "Get a Free Quote" — ROADMAP.md §8.6, built from
 * `reference/prototype-copy/quote-form.md` field by field.
 *
 * **This is not a contact form with an extra box.** It asks how many properties,
 * up to twenty, and then collects bedrooms, bathrooms and a square-footage band
 * for *each one*. That block is the entire basis of the quote the office writes
 * back — without it a property manager with twelve units submits something
 * indistinguishable from a one-bedroom flat.
 *
 * What is fixed on the way through, and every one of these is live on the
 * prototype today:
 *
 *   - **Every input has a `name` and a bound `<label for>`.** The prototype's
 *     forms have neither, so password managers cannot fill them.
 *   - **Validation on blur**, not on keystroke — design-system.md §8.
 *   - **44px on everything a finger has to hit**, including the select menus.
 *   - Placeholders show a *local* example: `Cedar City`, `84720`.
 */

interface PropertyRow {
  bedrooms: string;
  bathrooms: string;
  squareFeetBand: string;
}

function emptyProperty(): PropertyRow {
  // Pre-filled with the commonest answer rather than blank. Every field is
  // required, and making somebody set three dropdowns per property before they
  // can submit is how a twelve-property enquiry becomes a phone call instead.
  return { bedrooms: '3', bathrooms: '2.0', squareFeetBand: '1500_2000' };
}

const STATE_OPTIONS = US_STATES.map((state) => ({ value: state, label: state }));
const BEDROOM_SELECT = BEDROOM_OPTIONS.map((value) => ({ value, label: value }));
const PROPERTY_COUNT_OPTIONS = Array.from({ length: MAX_PROPERTIES }, (_, index) => ({
  value: String(index + 1),
  label: index === 0 ? '1 Property' : `${String(index + 1)} Properties`,
}));

export function QuoteForm() {
  /*
   * When the form was rendered, for the timing check.
   *
   * **Lazy initial state, not a ref.** It has to be stable — if it moved on
   * every render, each keystroke would reset the clock and the check would
   * never fire — and it also has to be *readable during render*, which a ref is
   * not. `useState` with an initialiser gives both: computed once, then a plain
   * value.
   */
  const [renderedAt] = useState(() => Date.now());

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [state, setState] = useState<string>(DEFAULT_STATE);
  const [notes, setNotes] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [properties, setProperties] = useState<PropertyRow[]>([emptyProperty()]);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const values = useMemo(
    () => ({
      name,
      email,
      phone,
      city,
      postalCode,
      state,
      notes: notes.trim() === '' ? undefined : notes,
      properties: properties.map((property) => ({
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        squareFeetBand: property.squareFeetBand,
      })),
      [HONEYPOT_FIELD]: honeypot,
      renderedAt,
    }),
    [name, email, phone, city, postalCode, state, notes, properties, honeypot, renderedAt],
  );

  const { errors, isValid, parsed, touch, touchAll, reset } = useBlurValidation(
    quoteRequestSchema,
    values,
  );

  function setCount(next: number) {
    setProperties((current) => {
      if (next === current.length) return current;
      // Growing keeps what is already filled in; shrinking drops from the end,
      // so changing 12 to 11 does not lose the first eleven.
      if (next > current.length) {
        return [...current, ...Array.from({ length: next - current.length }, emptyProperty)];
      }
      return current.slice(0, next);
    });
  }

  function updateProperty(index: number, patch: Partial<PropertyRow>) {
    setProperties((current) =>
      current.map((property, position) =>
        position === index ? { ...property, ...patch } : property,
      ),
    );
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);

    // Everything becomes visible, so pressing the button on an incomplete form
    // shows what is missing rather than doing nothing at all.
    touchAll();
    if (!isValid || parsed === null) return;

    setSubmitting(true);
    api
      .post<{ ok: true; email: string }>('/api/public/intake/quote', parsed)
      .then((result) => {
        setSubmitted(result.email);
        reset();
      })
      .catch(() => {
        // Named, with a way through. A dead end on the form a business receives
        // work through is a lost customer who has no idea why.
        setFailure(
          `Something went wrong sending that. Please try again, or ring us on ${NAP.phone}.`,
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  if (submitted !== null) {
    return (
      <Card>
        <div className="flex gap-3">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-status-completed-text" aria-hidden />
          <div>
            {/* `aria-live` so somebody using a screen reader is told the form
                submitted — the visual change alone says nothing. */}
            <h2 aria-live="polite" className="text-h2">
              Thanks — we have your request
            </h2>
            <p className="mt-2 text-body text-ink-700">
              We have sent a confirmation to <strong>{submitted}</strong>, and somebody from the
              office will be in touch shortly. If it is urgent, ring us on{' '}
              <a href={NAP.phoneHref} className="text-bark-700 underline">
                {NAP.phone}
              </a>
              .
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-h2">Get a free quote</h2>
      <p className="mt-2 text-body text-ink-700">
        Tell us about your properties and we&rsquo;ll get back to you with a quote!
      </p>
      <p className="mt-1 text-meta text-ink-500">Fields marked with * are required.</p>

      <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
        <FormError>{failure}</FormError>

        {/*
          The honeypot. Hidden from sight *and* from assistive technology, and
          named to look like something worth filling in. A human never sees it;
          a bot filling every field gives itself away.

          `tabIndex={-1}` and `aria-hidden` matter: a screen-reader user who
          could reach this would fill it in and be silently dropped, which is
          the worst version of this technique.
        */}
        <div aria-hidden="true" className="hidden">
          <label htmlFor="company-website-field">Company website</label>
          <input
            id="company-website-field"
            name={HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(event) => {
              setHoneypot(event.target.value);
            }}
          />
        </div>

        <fieldset className="space-y-5">
          <legend className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
            About you
          </legend>

          <Field
            label="Name"
            name="name"
            required
            autoComplete="name"
            placeholder="Your name"
            value={name}
            error={errors.name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            onBlur={() => {
              touch('name');
            }}
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              error={errors.email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              onBlur={() => {
                touch('email');
              }}
            />
            <Field
              label="Phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              // A *local* example — design-system.md §8.
              placeholder="(435) 000-0000"
              value={phone}
              error={errors.phone}
              onChange={(event) => {
                setPhone(event.target.value);
              }}
              onBlur={() => {
                touch('phone');
              }}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <Field
              label="City"
              name="city"
              required
              autoComplete="address-level2"
              placeholder="Cedar City"
              value={city}
              error={errors.city}
              onChange={(event) => {
                setCity(event.target.value);
              }}
              onBlur={() => {
                touch('city');
              }}
            />
            <Field
              label="Zip Code"
              name="postalCode"
              required
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="84720"
              value={postalCode}
              error={errors.postalCode}
              onChange={(event) => {
                setPostalCode(event.target.value);
              }}
              onBlur={() => {
                touch('postalCode');
              }}
            />
            <SelectField
              label="State"
              name="state"
              required
              autoComplete="address-level1"
              options={STATE_OPTIONS}
              value={state}
              error={errors.state}
              onChange={(event) => {
                setState(event.target.value);
              }}
              onBlur={() => {
                touch('state');
              }}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-5">
          <legend className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
            Your properties
          </legend>

          <SelectField
            label="Number of Properties"
            name="propertyCount"
            required
            options={PROPERTY_COUNT_OPTIONS}
            value={String(properties.length)}
            hint="Add a row for each place you would like cleaned."
            onChange={(event) => {
              setCount(Number(event.target.value));
            }}
          />

          {properties.map((property, index) => (
            <fieldset
              // Index is the identity here: these rows have no id of their own
              // and reordering is not possible, so it is stable for as long as
              // the row exists.
              key={index}
              className="space-y-4 rounded-md border border-ink-300 p-4"
            >
              <legend className="px-1.5 text-label text-bark-700">Property {index + 1}</legend>

              <div className="grid gap-4 sm:grid-cols-3">
                <SelectField
                  label="Bedrooms"
                  name={`properties.${String(index)}.bedrooms`}
                  required
                  options={BEDROOM_SELECT}
                  value={property.bedrooms}
                  error={errors[`properties.${String(index)}.bedrooms`]}
                  onChange={(event) => {
                    updateProperty(index, { bedrooms: event.target.value });
                  }}
                />
                <SelectField
                  label="Bathrooms"
                  name={`properties.${String(index)}.bathrooms`}
                  required
                  // Half steps, because half bathrooms are real — and the value
                  // is a string all the way to the `decimal` column so no float
                  // ever exists.
                  options={BATHROOM_OPTIONS}
                  value={property.bathrooms}
                  error={errors[`properties.${String(index)}.bathrooms`]}
                  onChange={(event) => {
                    updateProperty(index, { bathrooms: event.target.value });
                  }}
                />
                <SelectField
                  label="Sq Ft"
                  name={`properties.${String(index)}.squareFeetBand`}
                  required
                  options={SQUARE_FEET_BAND_OPTIONS}
                  value={property.squareFeetBand}
                  error={errors[`properties.${String(index)}.squareFeetBand`]}
                  onChange={(event) => {
                    updateProperty(index, { squareFeetBand: event.target.value });
                  }}
                />
              </div>
            </fieldset>
          ))}
        </fieldset>

        <TextareaField
          label="Additional Notes"
          name="notes"
          rows={4}
          placeholder="Any special requests, access instructions, or questions…"
          value={notes}
          error={errors.notes}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
          onBlur={() => {
            touch('notes');
          }}
        />

        <Button type="submit" size="lg" disabled={submitting}>
          {submitting ? 'Sending…' : 'Get My Free Quote'}
        </Button>
      </form>
    </Card>
  );
}
